import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface Photo {
  id: string;
  image_url: string;
  caption: string | null;
  uploaded_by: string;
  created_at: string;
}

interface UsePhotoManagementReturn {
  photos: Photo[];
  loading: boolean;
  error: string | null;
  deletePhoto: (photoId: string, imageUrl: string) => Promise<void>;
  refreshPhotos: () => Promise<void>;
}

export function usePhotoManagement(): UsePhotoManagementReturn {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('photos')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (fetchError) throw fetchError;
      setPhotos(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching photos:', err);
      setError('Failed to fetch photos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, []);

  const deletePhoto = async (photoId: string, imageUrl: string) => {
    try {
      const filePath = imageUrl.split('/').pop();
      if (filePath) {
        const { error: storageError } = await supabase.storage
          .from('photos')
          .remove([filePath]);
          
        if (storageError) throw storageError;
      }

      const { error: dbError } = await supabase
        .from('photos')
        .delete()
        .eq('id', photoId);
        
      if (dbError) throw dbError;

      setPhotos(photos.filter(photo => photo.id !== photoId));
      setError(null);
    } catch (err) {
      console.error('Error deleting photo:', err);
      throw new Error('Failed to delete photo');
    }
  };

  return {
    photos,
    loading,
    error,
    deletePhoto,
    refreshPhotos: fetchPhotos
  };
}